<template>
    <div v-if="dataLoaded">
        <h3 class="mb-4">
            Encuestas/Quices
            <b-button
                v-if="isUserStudenTeacherOrBetter"
                class="ml-2"
                variant="primary"
                @click="onCreate()"
            >
                <b-icon-plus /> Agregar
            </b-button>
            <b-button
                v-if="isUserStudenTeacherOrBetter"
                class="ml-2"
                variant="primary"
                @click="onDownload()"
                :disabled="!doEvaluatedClosedSurveysExist"
            >
                <b-icon-download /> Descargar Resultados
            </b-button>
        </h3>

        <b-tabs
            active-nav-item-class="bg-primary text-white"
            v-if="surveys && (surveys.survey.length || surveys.quiz.length)"
        >
            <!-- Surveys tab -->
            <b-tab
                v-if="surveys.survey.length"
                title="Encuestas"
            >
                <b-table
                    hover
                    responsive
                    striped
                    :items="surveys.survey"
                    :fields="surveysFields"
                    :sort-by="'name'"
                    :sort-desc="false"
                >
                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(name)="data"
                    >
                        <router-link
                            :to="{ name: 'site-teacher-surveys-detail', params: { surveyId: data.item.id } }"
                        >
                            {{ data.value }}
                        </router-link>
                    </template>

                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(state)="data"
                    >
                        <div style="font-size: 1.1rem;">
                            <span v-if="data.value === 'draft'">
                                <b-badge variant="info" class="py-2 px-3">Borrador</b-badge>
                            </span>
                            <span v-else-if="data.value === 'open'">
                                <b-badge variant="success" class="py-2 px-3">Abierto</b-badge>
                            </span>
                            <span v-else-if="data.value === 'closed'">
                                <b-badge variant="dark" class="py-2 px-3">Cerrado</b-badge>
                            </span>
                        </div>
                    </template>

                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(durationMinutes)="data"
                    >
                        <span v-if="data.value === 0">Indefinida</span>
                        <span v-else>{{ data.value }} mins.</span>
                    </template>

                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(actions)="data"
                    >
                        <div>
                            <b-button
                                variant="danger"
                                size="sm"
                                class="ml-2"
                                @click="onDelete(data.item)"
                            >
                                <b-icon-trash-fill />
                            </b-button>
                        </div>
                    </template>
                </b-table>
            </b-tab>

            <!-- Quizzes tab -->
            <b-tab
                v-if="surveys.quiz.length"
                title="Quices"
            >
                <b-table
                    hover
                    responsive
                    striped
                    :items="surveys.quiz"
                    :fields="surveysFields"
                    :sort-by="'name'"
                    :sort-desc="false"
                >
                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(name)="data"
                    >
                        <router-link
                            :to="{ name: 'site-teacher-surveys-detail', params: { surveyId: data.item.id } }"
                        >
                            {{ data.value }}
                        </router-link>
                    </template>

                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(state)="data"
                    >
                        <div style="font-size: 1.1rem;">
                            <span v-if="data.value === 'draft'">
                                <b-badge variant="info" class="py-2 px-3">Borrador</b-badge>
                            </span>
                            <span v-else-if="data.value === 'open'">
                                <b-badge variant="success" class="py-2 px-3">Abierto</b-badge>
                            </span>
                            <span v-else-if="data.value === 'closed'">
                                <b-badge variant="dark" class="py-2 px-3">Cerrado</b-badge>
                            </span>
                        </div>
                    </template>

                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(durationMinutes)="data"
                    >
                        <span v-if="data.value === 0">Indefinida</span>
                        <span v-else>{{ data.value }} mins.</span>
                    </template>

                    <template
                        v-if="isUserStudenTeacherOrBetter"
                        v-slot:cell(actions)="data"
                    >
                        <div>
                            <b-button
                                variant="danger"
                                size="sm"
                                class="ml-2"
                                @click="onDelete(data.item)"
                            >
                                <b-icon-trash-fill />
                            </b-button>
                        </div>
                    </template>
                </b-table>
            </b-tab>
        </b-tabs>
        <p v-else>No existe ninguna encuesta o quiz para este sitio web.</p>

        <!-- Create Survey Modal -->
        <SurveyCreateUpdateModal v-if="isUserStudenTeacherOrBetter" />
    </div>
</template>

<script>
import SurveyCreateUpdateModal from './components/SurveyCreateUpdateModal'
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
    name: 'TeachingStaffSurveysList',
    components: {
        SurveyCreateUpdateModal
    },
    data () {
        return {
            dataLoaded: false,
            surveyTypes: {
                survey: 'Encuestas',
                quiz: 'Quices'
            }
        }
    },
    computed: {
        ...mapState({
            activeSite: 'activeSite',
            selectedCrudObject: 'selectedCrudObject',
            surveys: 'activeSiteTeacherSurveys'
        }),
        ...mapGetters([
            'isUserStudenTeacherOrBetter'
        ]),
        surveysFields () {
            let fields = [
                { key: 'name', label: 'Nombre', sortable: true },
                { key: 'participants', label: 'Participantes', class: 'text-center' },
                { key: 'state', label: 'Estado', sortable: true, class: 'text-center' },
                { key: 'durationMinutes', label: 'Duración', class: 'text-center' }
            ]
            if (this.isUserStudenTeacherOrBetter) {
                fields.push({ key: 'actions', label: 'Acciones', class: 'text-center' })
            }
            return fields
        },
        doEvaluatedClosedSurveysExist () {
            if (this.surveys.quiz.length) {
                for (const survey of this.surveys.quiz) {
                    if (survey.state === 'closed') {
                        return true
                    }
                }
            }
            return false
        }
    },
    methods: {
        ...mapActions([
            'deleteSiteSurvey',
            'readSiteTeacherSurveys',
            'readAllSurveysDownloadData'
        ]),
        async loadSiteSurveys () {
            let loader = this.$loading.show()
            this.dataLoaded = false
            try {
                if (this.isUserStudenTeacherOrBetter) {
                    await this.readSiteTeacherSurveys(this.activeSite.id)
                }
                this.dataLoaded = true
            } catch (err) {
                console.log(err)
                this.dataLoaded = false
                this.unknownErrorToast()
            } finally {
                loader.hide()
            }
        },
        onCreate () {
            this.$store.commit('SET_SELECTED_CRUD_OBJECT', null)
            this.$bvModal.show('modal-create-survey')
        },
        async onDownload () {
            try {
                var blobData = await this.readAllSurveysDownloadData(this.activeSite.id)
                var fileURL = window.URL.createObjectURL(new Blob([blobData]))
                var fileLink = document.createElement('a')
                fileLink.href = fileURL
                fileLink.setAttribute(
                    'download',
                    `Resultados de Quices de ${this.activeSite.course.name} ${this.activeSite.semester.academic_period}.xlsx`
                )
                document.body.appendChild(fileLink)
                fileLink.click()
            } catch (err) {
                console.log(err)
                this.$root.unknownErrorToast()
            }
        },
        async onDelete (surveyObject) {
            this.$store.commit('SET_SELECTED_CRUD_OBJECT', surveyObject)
            var modalBodyText = this.$createElement('p', [
                '¿Esta seguro que desea eliminar esta encuesta/quiz? ' +
                'Esto también eliminará todas las preguntas, opciones y ' +
                'respuestas de los estudiantes relacionadas.',
                this.$createElement('br'),
                this.$createElement('br'),
                'Si en cambio lo que desea es evitar que los estudiantes puedan ver ' +
                'esta encuesta/quiz, puede cerrarla.'
            ])
            try {
                let value = await this.$bvModal.msgBoxConfirm([modalBodyText], {
                    title: 'Confirmar',
                    okVariant: 'danger',
                    okTitle: 'Confirmar',
                    cancelTitle: 'Cancelar',
                    centered: true
                })
                if (value) {
                    await this.onConfirmDelete()
                } else {
                    this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
                }
            } catch (error) {
                console.log({ error: error })
                this.unknownErrorToast()
            }
        },
        async onConfirmDelete () {
            var loader = this.$loading.show()
            try {
                const payloadData = { courseSiteId: this.activeSite.id, surveyId: this.selectedCrudObject.id }
                await this.deleteSiteSurvey(payloadData)
                await this.loadSiteSurveys()
                this.$root.infoToast('La encuesta/quiz ha sido eliminada exitosamente.')
            } catch (error) {
                console.log({ error: error })
                this.$root.dangerToast('No se pudo eliminar la encuesta/quiz, intente más tarde.')
            } finally {
                loader.hide()
                this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
            }
        }
    },
    async created () {
        this.$store.commit('RESET_SELECTED_CRUD_OBJECT')
        await this.loadSiteSurveys()
    },
    async destroyed () {
        this.$store.commit('RESET_ACTIVE_SITE_SURVEYS')
    }
}
</script>
